import { Allergen } from "../../../admin/products/productsTypes"
import { DaysOfWeekType } from "../menuPlanningTypes"

/** The API draft mixes numeric and string ids (e.g. "tpl_01HZQ8P2"), so accept both. */
export type MenuTemplateId = number | string

export enum MenuTemplateChannel {
    MOBILE_APP = "MOBILE_APP",
    CANTEEN_DISPLAY = "CANTEEN_DISPLAY",
    DIGITAL_SIGNS = "DIGITAL_SIGNS",
    PRINT = "PRINT",
}

export enum DishParameterKey {
    TITLE = "TITLE",
    PRICE = "PRICE",
    DESCRIPTION = "DESCRIPTION",
    ALLERGENS = "ALLERGENS",
    AVAILABILITY = "AVAILABILITY",
    MARKETING_TAG = "MARKETING_TAG",
    IMAGE = "IMAGE",
    INGREDIENTS = "INGREDIENTS",
    NUTRITION = "NUTRITION",
    CO2_FOOTPRINT = "CO2_FOOTPRINT",
}

export enum DishParameterType {
    LOCKED = "LOCKED",
    PREFILLED = "PREFILLED",
    MANDATORY = "MANDATORY",
    OPTIONAL = "OPTIONAL",
}

export type MenuTemplateListItem = Readonly<{
    id: MenuTemplateId
    name: string
    channels?: MenuTemplateChannel[]
    categoryCount: number
    dishCount: number
    updatedAt?: string
    updatedBy?: Readonly<{
        id: number
        name: string
    }>
}>

export type DishParameter = Readonly<{
    key: DishParameterKey
    type: DishParameterType
    defaultValue?: string
    /** PRICE only: the price is the same every day of the week. */
    sameAllWeek?: boolean
    /** AI-capable inputs only: whether week-menu AI generation may fill this input (default true). */
    aiFill?: boolean
}>

export type MenuTemplateDish = Readonly<{
    name: string
    availability?: Readonly<{
        weekDays: DaysOfWeekType[]
    }>
    parameters: DishParameter[]
}>

export type MenuTemplateCategory = Readonly<{
    name: string
    dishes: MenuTemplateDish[]
}>

export type MenuTemplateDetails = Readonly<{
    id: MenuTemplateId
    merchant?: Readonly<{
        id: number
        name: string
    }>
    name: string
    isActive?: boolean
    channels?: MenuTemplateChannel[]
    categories: MenuTemplateCategory[]
}>

/** Per-input template configuration shared by all six dish inputs. */
export type DishFieldConfig = Readonly<{
    /** "Do not show" from the task — a hidden input is not part of the week menu. */
    isShown: boolean
    type: DishParameterType
}>

export type DishFormValues = Readonly<{
    name: string
    /** The dish title shown in the week menu, configurable separately from the internal name. */
    title: DishFieldConfig & Readonly<{ value: string; aiFill: boolean }>
    price: DishFieldConfig & Readonly<{ value?: number; sameAllWeek: boolean; aiFill: boolean }>
    description: DishFieldConfig & Readonly<{ value: string; aiFill: boolean }>
    allergens: DishFieldConfig & Readonly<{ value: Allergen[]; aiFill: boolean }>
    /** Weekday availability; the weekdays are the template value (defaults to every weekday). */
    availability: DishFieldConfig & Readonly<{ weekDays: DaysOfWeekType[] }>
    marketingTag: DishFieldConfig & Readonly<{ value: string; aiFill: boolean }>
    image: DishFieldConfig & Readonly<{ photo?: { uid?: string; url?: string } }>
    /** The "Details" fields, shown in a dialog in the week menu; no AI fill. */
    ingredients: DishFieldConfig & Readonly<{ value: string }>
    nutrition: DishFieldConfig & Readonly<{ value: string }>
    /** Kept as the raw input string; the payload turns it into a number-like default. */
    co2Footprint: DishFieldConfig & Readonly<{ value: string }>
}>

export type CategoryFormValues = Readonly<{
    name: string
    dishes: DishFormValues[]
}>

export type MenuTemplateFormValues = Readonly<{
    name: string
    channels: MenuTemplateChannel[]
    categories: CategoryFormValues[]
}>
